import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { startCase } from "lodash";
import {
  Box,
  Typography,
  Alert,
  Stack,
  Pagination,
  CircularProgress,
  IconButton,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Popover,
  MenuItem,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  AutoFixHigh as AutoFixHighIcon,
  FilterAlt as FilterAltIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import NewFieldDialog from "./NewFieldDialog";

const fieldTypeMapper = {
  PDFTextField: "text",
  PDFCheckBox: "checkboxGroup",
};

const locationConfig = {
  sign: {
    borderLeft: "3px solid #1976d2",
    chipColor: "primary",
    chipLabel: "Auto • Sign",
  },
  create: {
    borderLeft: "3px solid #2e7d32",
    chipColor: "success",
    chipLabel: "Manual • Create",
  },
};

const getLocationStyle = (location) =>
  locationConfig[location] ?? { borderLeft: "1px solid #e0e0e0" };

const FormFieldPanel = ({
  currentProcessingFile,
  setCurrentProcessingFile,
  onNotify,
  loading: loadingDoc,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const [openNewFieldDialog, setOpenNewFieldDialog] = useState(false);
  const [openDeleteFieldDialog, setOpenDeleteFieldDialog] = useState(false);
  const [openCreateFieldDialog, setOpenCreateFieldDialog] = useState(false);
  const [selectedField, setSelectedField] = useState(null);

  const [fields, setFields] = useState([]);
  const [searchField, setSearchField] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [createFieldMessage, setCreateFieldMessage] = useState({
    type: "",
    message: "",
  });

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filter, setFilter] = useState({});

  const debounceTimerRef = useRef(null);

  useEffect(() => {
    if (error) {
      const interv = setTimeout(() => {
        setError(false);
      }, 5000);
      return () => clearTimeout(interv);
    }
  }, [error]);

  useEffect(() => {
    if (createFieldMessage) {
      const interv = setTimeout(() => {
        setCreateFieldMessage({
          type: "",
          message: "",
        });
      }, 5000);
      return () => clearTimeout(interv);
    }
  }, [createFieldMessage]);

  const getFormFields = useCallback(
    async (filter, page, searchQuery) => {
      setLoading(true);
      setError("");

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/tree`,
          { currentSession, ...filter, page, limit: 7, query: searchQuery },
          { withCredentials: true },
        );

        if (res.data.success) {
          const fields = res.data.data?.fields;
          setFields(fields);
          setTotalPage(res.data.data?.total_pages);
        }
      } catch (error) {
        setError(
          error?.response?.data?.message ||
            "An error occurred while fetching fields",
        );
      } finally {
        setLoading(false);
      }
    },
    [currentSession],
  );

  const handlePageChange = (e, value) => {
    setPage(value);
    getFormFields(filter, value, searchField);
  };

  useEffect(() => {
    getFormFields({}, 1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseNewFieldDialog = () => {
    setOpenNewFieldDialog(false);
    setSelectedField(null);
  };

  const handleCloseDeleteFieldDialog = () => {
    setOpenDeleteFieldDialog(false);
    setSelectedField(null);
  };

  const handleCloseCreateFieldDialog = () => {
    setOpenCreateFieldDialog(false);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/delete`,
        {
          field_id: selectedField?.field_id,
          currentSession: currentSession,
        },
        {
          withCredentials: true,
        },
      );

      if (response?.data?.success) {
        getFormFields(filter, page, searchField.trim());
        handleCloseDeleteFieldDialog();
      }
    } catch (error) {
      setError(
        error?.response?.data?.message ||
          `An error occurred while deleting field`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAttach = (e, field) => {
    e.stopPropagation();

    const tempFields = currentProcessingFile?.document?.tempFields;
    if (tempFields?.selectedExtraFields?.length > 1) {
      onNotify?.(
        "error",
        `You have selected more than one field. Only a single field can be assigned at a time.`,
      );
      return;
    }

    const selectedField = tempFields?.selectedExtraFields?.[0] ?? {};

    const isFieldAlreadyAssigned = tempFields?.formFields?.find(
      (f) => f?.field_id === field?.field_id && f?.name !== selectedField?.name,
    );
    if (isFieldAlreadyAssigned) {
      onNotify?.(
        "error",
        `${field?.field_label} is already assigned to this form. Please choose a different field.`,
      );
      return;
    }

    const updatedFormFields = tempFields?.formFields?.map((f) => {
      if (selectedField?.name === f?.name) {
        return {
          ...f,
          field_id: field?.field_id,
        };
      } else {
        return f;
      }
    });
    const updatedSelectedFields = [
      {
        ...selectedField,
        field_id: field?.field_id,
      },
    ];

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: updatedFormFields,
          selectedExtraFields: updatedSelectedFields,
        },
      },
    }));
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setSearchField(val);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      getFormFields(filter, 1, val.trim());
      setPage(1);
    }, 1000);
  };

  const handleCreateSelectedFields = async () => {
    setLoading(true);
    setCreateFieldMessage({ type: "", message: "" });

    const tempFields = currentProcessingFile?.document?.tempFields;
    const fieldsArray = tempFields?.selectedExtraFields;

    const succeeded = [];
    const failed = [];
    const total = fieldsArray.length;
    for (let i = 0; i < total; i++) {
      const fieldValues = fieldsArray[i];
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/upsert`,
          {
            currentSession,
            field: {
              field_label: fieldValues?.field_label,
              field_type: fieldTypeMapper[fieldValues?.type],
              location: "create",
            },
          },
          { withCredentials: true },
        );
        if (response?.data?.success) {
          succeeded.push({ index: i, field: fieldValues, data: response.data });
        } else {
          const err = {
            index: i,
            field: fieldValues,
            message: response?.data?.message || "Unknown error",
          };
          failed.push(err);
        }
      } catch (error) {
        const err = {
          index: i,
          field: fieldValues,
          message:
            error?.response?.data?.message ||
            `An error occurred while creating field`,
        };
        failed.push(err);
      }
    }
    // Refresh fields list once after all iterations
    if (succeeded.length > 0) {
      await getFormFields(filter, 1, searchField.trim());
      setPage(1);
    }
    // Report a summary message
    if (failed.length === 0) {
      setCreateFieldMessage({
        type: "success",
        message: `All ${succeeded.length} field(s) created successfully.`,
      });
    } else if (succeeded.length === 0) {
      setCreateFieldMessage({
        type: "error",
        message: `All ${failed.length} field(s) failed. First error: ${failed[0].message}`,
      });
    } else {
      setCreateFieldMessage({
        type: "warning",
        message: `${succeeded.length} succeeded, ${failed.length} failed. First error: ${failed[0].message}`,
      });
    }

    handleCloseCreateFieldDialog();
    setLoading(false);
  };

  const handleClickFilter = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          height: "calc(100% - 50px)",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: "1px solid #F3F4F6",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 16, fontWeight: 600, color: "#111827" }}
            >
              Form Field Browser
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            px: 1.5,
            py: 1.25,
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            size="small"
            startIcon={<AutoFixHighIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              setOpenCreateFieldDialog(true);
            }}
            color="primary"
            disabled={
              !currentProcessingFile?.document?.tempFields?.selectedExtraFields
                ?.length || loadingDoc
            }
          >
            Auto Create Field
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={loadingDoc}
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              setOpenNewFieldDialog(true);
            }}
            color="primary"
          >
            New Field
          </Button>
        </Box>

        <Box
          display={"flex"}
          alignItems={"center"}
          gap={1}
          sx={{ px: 1.5, pb: 1.25 }}
        >
          <TextField
            id="searchInput"
            value={searchField}
            onChange={(e) => handleChange(e)}
            size="small"
            placeholder="Search assigned fields..."
            sx={{
              "& .MuiInputBase-input": { fontSize: 14 },
              flex: 1,
            }}
            InputProps={{
              sx: { padding: "0px 10px" },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            variant="outlined"
          />
          <IconButton
            size="small"
            onClick={handleClickFilter}
            disabled={loadingDoc}
            sx={{
              border: "1px solid #d0d7de",
              borderRadius: 1.5,
              backgroundColor: "transparent",
              p: "4.41px !important",
            }}
          >
            <FilterAltIcon
              sx={{
                fontSize: "0.95rem",
              }}
            />
          </IconButton>
        </Box>
        {(error || createFieldMessage?.message) && (
          <Alert
            severity={error ? "error" : createFieldMessage?.type}
            sx={{ borderRadius: 2, mx: 1.5, mb: 1.25 }}
          >
            {error || createFieldMessage?.message}
          </Alert>
        )}

        {loading ? (
          <Box
            sx={{ px: 1.5, display: "flex", justifyContent: "center", mt: 2.5 }}
          >
            <CircularProgress color="warning" size="3rem" />
          </Box>
        ) : fields.length === 0 ? (
          <Box sx={{ px: 1.5 }}>
            <Typography
              color="primary"
              variant="h6"
              mb={2}
              mt={2.5}
              sx={{ fontSize: "1rem" }}
              align="center"
            >
              You have no fields to display just yet
            </Typography>
          </Box>
        ) : (
          <>
            <Box flex={1} sx={{ px: 1.5, overflow: "auto" }}>
              <Stack gap={0.75}>
                {fields?.map((field, index) => {
                  return (
                    <Accordion
                      key={field?.field_id}
                      disableGutters
                      elevation={0}
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "12px !important",
                        overflow: "hidden",
                        borderLeft: getLocationStyle(field?.location)
                          .borderLeft,
                        "&:before": { display: "none" },
                        "&.Mui-expanded": {
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      <AccordionSummary
                        component="div"
                        expandIcon={
                          <ExpandMoreIcon sx={{ fontSize: "1.5rem" }} />
                        }
                        sx={{
                          gap: 2,
                        }}
                        slotProps={{
                          content: {
                            sx: {
                              display: "flex",
                              flexDirection: "column",
                            },
                          },
                        }}
                      >
                        <Box
                          display={"flex"}
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: "0.85rem",
                                color: "rgba(0, 0, 0, 0.8)",
                                fontWeight: 500,
                              }}
                            >
                              {field?.field_label}
                            </Typography>
                            {field?.location && (
                              <Chip
                                label={startCase(field.location)}
                                size="small"
                                variant="filled"
                                color={
                                  getLocationStyle(field.location).chipColor
                                }
                                sx={{
                                  fontSize: "0.7rem",
                                  height: 20,
                                  borderRadius: 1,
                                }}
                              />
                            )}
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => handleAttach(e, field)}
                            sx={{
                              border: "1px solid #d0d7de",
                              borderRadius: 1.5,
                              backgroundColor: "transparent",
                              p: "4.41px !important",
                            }}
                            disabled={loadingDoc}
                          >
                            <AttachFileIcon
                              sx={{
                                fontSize: "0.95rem",
                                transform: "rotate(45deg)",
                              }}
                            />
                          </IconButton>
                        </Box>
                      </AccordionSummary>

                      <AccordionDetails>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.75,
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box display={"flex"} alignItems={"center"} gap={1}>
                            {field?.field_type && (
                              <Chip
                                label={startCase(field.field_type)}
                                size="small"
                                variant="filled"
                                sx={{
                                  fontSize: "0.7rem",
                                  height: 20,
                                  borderRadius: 1,
                                }}
                              />
                            )}
                          </Box>
                          <Box display={"flex"} alignItems={"center"} gap={1}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenNewFieldDialog(true);
                                setSelectedField(field);
                              }}
                              disabled={loadingDoc}
                              sx={{
                                border: "1px solid #d0d7de",
                                borderRadius: 1.5,
                                backgroundColor: "transparent",
                                p: "4px !important",
                              }}
                            >
                              <EditIcon sx={{ fontSize: "1rem" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDeleteFieldDialog(true);
                                setSelectedField(field);
                              }}
                              disabled={loadingDoc}
                              sx={{
                                border: "1px solid",
                                borderColor: "error.light",
                                borderRadius: 1.5,
                                backgroundColor: "transparent",
                                p: "4px !important",
                              }}
                            >
                              <DeleteIcon
                                sx={{ fontSize: "1rem" }}
                                color="error"
                              />
                            </IconButton>
                          </Box>
                        </Box>
                        {["checkboxGroup", "selectGroup"].includes(
                          field?.field_type,
                        ) &&
                          field?.children?.length > 0 && (
                            <Box
                              sx={{
                                mt: 1.25,
                                pt: 1.25,
                                borderTop: "1px dashed #e0e0e0",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.7rem",
                                  color: "rgba(0, 0, 0, 0.45)",
                                  fontWeight: 600,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                  mb: 0.75,
                                  display: "block",
                                }}
                              >
                                Options
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.5,
                                }}
                              >
                                {field?.children.map((option, index) => (
                                  <Accordion
                                    key={option?.field_id ?? index}
                                    disableGutters
                                    elevation={0}
                                    sx={{
                                      border: "1px solid #e0e0e0",
                                      borderRadius: "8px !important",
                                      overflow: "hidden",
                                      "&:before": { display: "none" },
                                      "&.Mui-expanded": {
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                      },
                                    }}
                                  >
                                    <AccordionSummary
                                      expandIcon={
                                        <ExpandMoreIcon
                                          sx={{ fontSize: "1.2rem" }}
                                        />
                                      }
                                      sx={{
                                        gap: 1.5,
                                        minHeight: "36px !important",
                                      }}
                                      slotProps={{
                                        content: {
                                          sx: {
                                            display: "flex",
                                            flexDirection: "column",
                                            my: "8px !important",
                                          },
                                        },
                                      }}
                                    >
                                      <Box
                                        display="flex"
                                        justifyContent={"space-between"}
                                        alignItems="center"
                                        gap={0.5}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontSize: "0.78rem",
                                            color: "rgba(0, 0, 0, 0.8)",
                                            fontWeight: 500,
                                            lineHeight: 1.4,
                                          }}
                                        >
                                          {option?.field_label}
                                        </Typography>
                                      </Box>
                                    </AccordionSummary>

                                    <AccordionDetails
                                      sx={{ pt: 0.5, pb: 1, px: 1.5 }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 0.75,
                                          flexWrap: "wrap",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                        }}
                                      >
                                        {option?.location && (
                                          <Chip
                                            label={startCase(option.location)}
                                            size="small"
                                            variant="filled"
                                            color={
                                              getLocationStyle(option.location)
                                                .chipColor
                                            }
                                            sx={{
                                              fontSize: "0.7rem",
                                              height: 20,
                                              borderRadius: 1,
                                            }}
                                          />
                                        )}
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                          justifyContent="flex-end"
                                        >
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleAttach(e, option);
                                            }}
                                            disabled={loadingDoc}
                                            sx={{
                                              border: "1px solid #d0d7de",
                                              borderRadius: 1.5,
                                              backgroundColor: "transparent",
                                              p: "4px !important",
                                            }}
                                          >
                                            <AttachFileIcon
                                              sx={{
                                                fontSize: "0.85rem",
                                                transform: "rotate(45deg)",
                                              }}
                                            />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedField(option);
                                              setOpenNewFieldDialog(true);
                                            }}
                                            disabled={loadingDoc}
                                            sx={{
                                              border: "1px solid #d0d7de",
                                              borderRadius: 1.5,
                                              backgroundColor: "transparent",
                                              p: "4px !important",
                                            }}
                                          >
                                            <EditIcon
                                              sx={{ fontSize: "0.85rem" }}
                                            />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenDeleteFieldDialog(true);
                                              setSelectedField(option);
                                            }}
                                            disabled={loadingDoc}
                                            sx={{
                                              border: "1px solid",
                                              borderColor: "error.light",
                                              borderRadius: 1.5,
                                              backgroundColor: "transparent",
                                              p: "4px !important",
                                            }}
                                          >
                                            <DeleteIcon
                                              sx={{ fontSize: "0.85rem" }}
                                              color="error"
                                            />
                                          </IconButton>
                                        </Box>
                                      </Box>
                                    </AccordionDetails>
                                  </Accordion>
                                ))}
                              </Box>
                            </Box>
                          )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            </Box>
            <Box sx={{ py: 1.5, display: "flex", justifyContent: "center" }}>
              <Pagination
                count={totalPage}
                page={page}
                onChange={handlePageChange}
                siblingCount={0}
                size={"small"}
              />
            </Box>
          </>
        )}
      </Box>
      {openNewFieldDialog && (
        <NewFieldDialog
          open={openNewFieldDialog}
          handleClose={handleCloseNewFieldDialog}
          getFields={getFormFields}
          selectedField={selectedField}
          searchField={searchField}
          fields={fields}
          filter={filter}
          setPage={setPage}
        />
      )}
      <Dialog
        open={openDeleteFieldDialog}
        onClose={handleCloseDeleteFieldDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to remove{" "}
            <strong>{selectedField?.field_label}</strong>{" "}
            {selectedField?.field_type === "option" ? "option" : "field"}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteFieldDialog}
            disabled={loading}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleDeleteConfirm}
            loading={loading}
            variant="contained"
            color="error"
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openCreateFieldDialog}
        onClose={handleCloseCreateFieldDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to create selected fields?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseCreateFieldDialog}
            disabled={loading}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleCreateSelectedFields}
            loading={loading}
            variant="contained"
            color="error"
          >
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Popover
        id={Boolean(filterAnchorEl) ? "simple-popover" : undefined}
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Stack p={2} gap={1}>
          <Typography sx={{ fontWeight: 500 }}>Filter</Typography>
          <TextField
            label="Location"
            size="small"
            disabled={loading}
            select
            value={filter?.location ?? ""}
            sx={{ minWidth: "200px" }}
            onChange={(event) => {
              setFilter((prev) => ({ ...prev, location: event.target.value }));
            }}
          >
            <MenuItem value="create">Create</MenuItem>
            <MenuItem value="sign">Sign</MenuItem>
          </TextField>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              size="small"
              disabled={loading}
              onClick={() => {
                setFilter({});
                getFormFields({}, 1, searchField);
                setPage(1);
                handleCloseFilter();
              }}
              color="primary"
            >
              Clear Filter
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={loading}
              onClick={() => {
                getFormFields(filter, 1, searchField.trim());
                setPage(1);
                handleCloseFilter();
              }}
              color="primary"
            >
              Apply
            </Button>
          </Box>
        </Stack>
      </Popover>
    </>
  );
};

export default FormFieldPanel;
